import { LitElement, html, nothing } from 'lit';
import { property, customElement, query, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { ActionsDetails, CategoriesItems, CategoryItem } from '../types';
import { isScrollable } from '../../../utils/htmlElement.utils';
import { ImageState } from '../../../ui/gzImageRepo/types';
import { FontSizes, FontWeights } from '../../../types/common/types';
import { translateFn } from '../../../utils/translator.helper';
import _ from 'lodash';
import { GzAccordionWebComponent } from '../../../ui/gzAccordion';
import { ifDefined } from 'lit/directives/if-defined.js';
import { GzIconWebComponent } from '../../../ui/gzIcon';
import { ButtonColor, FontSize } from '../../../ui/gzIconButton/types';
import { entityIconSet } from '../../../theme/entity-icons';
import { GzIconLabelWebComponent } from '../../../ui/gzIconLabel';

/**
 * @summary Type here the description of this component
 * @tag categoriesSideMenu
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('categories-side-menu')
export class CategoriesSideMenuWebComponent extends LitElement {
  /**
   *
   * the title of the component
   */
  @property({ type: String })
  title = '';

  /**
   *
   */
  @property({ type: String })
  companyId;

  @property({
    type: Array,
    attribute: false,
    reflect: false,
  })
  initialCategories = null as null | Array<CategoriesItems>;

  /**
   *
   */
  @property({ type: String })
  public searchCategoriesPlaceholder;

  @property({
    type: Array,
    attribute: false,
    reflect: false,
  })
  actionsDetails = null as null | Array<ActionsDetails>;

  /**
   *
   * type category to scroll to
   */
  @property({ type: Number })
  public categoryToScroll;

  @property({
    type: Boolean,
    attribute: true,
    reflect: true,
  })
  hasErrors = false;

  /**
   * @internal
   */
  @query('.panel-content')
  panelContent: HTMLElement;

  /**
   * @internal
   * @type {Array<CategoriesItems> | null}
   */
  @state()
  categories: any;

  /**
   * @internal
   */
  @state()
  private searchText;

  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  render() {
    return html`
      ${this.renderHeader()}
      <div class="panel-content">${this.renderCategories()}</div>
    `;
  }

  renderHeader() {
    return html`
      <div class="header mt-6 mb-1">
        <gz-title class="mb-2" fontsize="16" fontweight="500">${this.title}</gz-title>
        <gz-input-search
          @change="${this.handleInputChange}"
          placeholder="${this.searchCategoriesPlaceholder}"
          id="views-input-search"
        ></gz-input-search>
      </div>
    `;
  }

  updated(changedProperties) {
    if (changedProperties.has('initialCategories') && this.initialCategories) {
      this.categories = _.cloneDeep(this.initialCategories);
    }

    if (changedProperties.get('searchText') !== undefined) {
      //force the setting of isOpen on child component on update (without this the render function for the child
      // accordion and the setting of isOpen would not be set)
      const gzAccordions = this.shadowRoot?.querySelectorAll('gz-accordion');
      if (gzAccordions) {
        this.shadowRoot?.querySelectorAll('gz-accordion').forEach((elemNode: GzAccordionWebComponent): void => {
          if (elemNode.getAttribute('forceIsOpen')) {
            elemNode.isOpen = true;
          }
        });
      }
    }

    this.updateComplete.then(() => this.scrollToCategory());
  }

  renderCategories() {
    if (this.hasErrors) {
      return this.renderErrorDeviceDetailsState();
    }

    if (!this.categories) {
      return html` <gz-loader></gz-loader>`;
    }

    if (this.categories.length === 0) {
      return nothing;
    }

    return html`${this.categories.map(category =>
      this.searchText?.length !== 0 && !category.items.length ? nothing : this.renderAccordion(category)
    )}`;
  }

  renderAccordion(category) {
    const gzIconElement = new GzIconLabelWebComponent();
    gzIconElement.icon = category.icon;
    gzIconElement.size = FontSize.ICON_SIZE_20;
    gzIconElement.iconColor = 'var(--chart-300-icon-buttons)';
    gzIconElement.setCustomIconSet(entityIconSet);

    return html` <gz-accordion
      ?isOpen="${category.isOpen ? category.isOpen : true}"
      forceIsOpen=${ifDefined(category.isOpen)}
      isCompact="true"
      align="left"
      id="${category.type}"
      textId="${category.text}"
    >
      <span slot="dynamic-title-content" class="accordion-slot d-inline-flex align-items-center">
        ${gzIconElement}
        <gz-tooltip class="accordion-title" tooltiptext="${category.text}" isellipsis="true"
          >${category.text}</gz-tooltip
        >
        <gz-text class="accordion-count" fontWeight="300" fontSize="10">(${category.items.length})</gz-text>
      </span>
      ${this.renderAccordionItems(category)}
    </gz-accordion>`;
  }

  renderAccordionItems(category: CategoriesItems) {
    return html` ${category.items.map(
      (item, index) => html` <div class="d-flex w-full accordion-item">
        <gz-tooltip class="flex-2" tooltipText="${item.name}" isEllipsis>
          <gz-text fontSize="12" class="flex-2 label-text d-inline">${item.name}</gz-text>
        </gz-tooltip>
        ${this.renderActionItems(category, item, index)}
      </div>`
    )}`;
  }

  renderActionItems(category, item, index) {
    if (!this.actionsDetails) {
      return nothing;
    }

    if (!this.isDropDownEventsAvailableForCategory(category.type)) {
      return nothing;
    }

    const options = this.actionsDetails.map(action => {
      return {
        title: action.title,
        value: action.value,
      };
    });

    return html` <gz-select
      class="side-menu-select"
      options="${JSON.stringify(options)}"
      @selectionChanged=${e => this.handleSelectionChanged(e.detail.selected, category.type, item, index)}
    >
      <gz-icon-button icon="ds-icon-more" fontSize="24" buttonType="square" buttonFill="quiet" buttonColor="secondary">
      </gz-icon-button>
    </gz-select>`;
  }

  isDropDownEventsAvailableForCategory(categoryType) {
    return true;
  }

  handleSelectionChanged(selectedOption: number, type: number, item: CategoryItem, index: number) {
    const selectedAction = this.actionsDetails?.find(action => {
      return action.value === selectedOption;
    });

    if (!selectedAction) {
      return;
    }

    selectedAction.actionCallback(type, item, index, this.companyId);
  }

  handleInputChange(event) {
    const searchInput = event.detail.sourceEvent.trim().toLowerCase();
    this.searchText = searchInput;
    this.filterCategories(searchInput);
  }

  /**
   *
   * @param filter
   */
  filterCategories(filter) {
    if (!this.initialCategories) {
      return;
    }

    const clonedInitialCategories = _.cloneDeep(this.initialCategories);

    const filteredCategories: CategoriesItems[] = [];
    for (const category of clonedInitialCategories) {
      const matchedItems = category.items.filter(item => {
        return item.name.trim().toLowerCase().includes(filter);
      });

      if (matchedItems.length > 0) {
        category.items = matchedItems;
        category.isOpen = true;
        filteredCategories.push(category);
      }
    }

    this.categories = filteredCategories;
  }

  renderErrorDeviceDetailsState() {
    return html` <gz-image-repo state="${ImageState.ERROR}">
      <gz-text fontWeight="${FontWeights.NORMAL}" fontSize="${FontSizes.PARAGRAPH_12}"
        >${translateFn(
          'Data loading has failed. Please refresh the page. If the issue persists, contact Bitdefender Enterprise Support.'
        )}
      </gz-text>
    </gz-image-repo>`;
  }

  scrollToCategory() {
    if (isScrollable(this.panelContent) && this.searchText === undefined) {
      const category = this.shadowRoot?.getElementById(this.categoryToScroll);
      const categoryPosition = category?.offsetTop;
      const offset = 124;

      if (categoryPosition != null) {
        this.panelContent.scrollTop = categoryPosition - offset;
      }
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'categories-side-menu': CategoriesSideMenuWebComponent;
  }
}
